import 'core-js';
import 'classlist-polyfill';
import 'intersection-observer';
import 'currentscript';
import 'library-@/utils/windowOpenWithNoopener';
import { container } from './inversify.config';
import _ from 'lodash';
import Vue from 'vue';
import init from './init';
import i18n from './locales';
import setTitle from './utils/setTitle';
import PatentView from './PatentView.vue';
import store from './vuex/store';
import router, { setDualViewRedirect, setRootRedirect } from './router';
import '@patsnap-biz/authority/src/style/authority.scss';
import './assets/styles/el-overwrites/index.scss';
import '@patsnap/common-snap-zen/src/scss/snap-zen.scss';
import '@/assets/styles/app/index.scss';
import 'library-@/highlight/scss/index.scss';
import '@patsnap-biz/design-tokens/scss/_variables-IP.css';
import 'vue-resize/dist/vue-resize.css';
import '@patsnap-biz/design-tokens/scss/_input.scss';
import './assets/styles/main.scss';
import createBridge, { createEventHub } from '@patsnap-utils/store-connect';
import PatentUtils from '@/utils/patent_utils';
import { initTransformCodeInterceptor } from 'library-@/apis/interceptors/transform_code.interceptor';
import { getIsFreeView } from 'library-@/utils/patent-view-free-judgment-util';
import { getAnalyticsDualViewSetting } from 'library-@/apis/core_basic_api/view';
import { getDualViewDefaultTab } from './utils/dual-view-setting';
import { fixZpreface } from 'library-@/startup';
import { getBatchUsedByAnchorApi } from 'library-@/apis/lab_api/lab';
import getParams from 'library-@/utils/get-params';
import hideHeaderHandler from '@/utils/hideHeaderHandler';
import { getIsFamilySearch, getIsFamilySearchWithUngroup } from '@/composables/use-family-search';
import { PatsnapCopilotIntentRendererRegistry } from '@analytics/patsnap-copilot/src/patsnap-copilot-renderer';
import { DualViewSettingService } from '@/services/dual-view/service/dual-view-setting.service';
import { PermissionService } from '@/services/permission/service/permission.service';
import type { DualViewSetting } from '@/services/dual-view/type/dual-view.type';
import type { Permission } from '@/services/permission/type/permission.type';
import { setContainer } from '@patsnap/tahiti-core/ioc/inject';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';

setContainer(container);
injectService<PatsnapCopilotIntentRendererRegistry>(PatsnapCopilotIntentRendererRegistry).onStart();
const dualViewSettingService = injectService<DualViewSettingService>(DualViewSettingService);
const permissionService = injectService<PermissionService>(PermissionService);

const { get, isEmpty } = _;

// 同族库搜索模式
const isFamilySearch = getIsFamilySearch();
const isFamilySearchWithUngroup = getIsFamilySearchWithUngroup();

// 建立跟fundation的连接
window.$bridge = createBridge({
    headerConfig: {
        publicModule: true,
        moduleName: 'patentView',
        inputType: (isFamilySearch || isFamilySearchWithUngroup) ? 'noInput' : undefined
    }
});
Vue.prototype.$eventHub = createEventHub(window.$bridge);
Vue.config.productionTip = false;

const startup = async () => {
    // 如果是inno跳转过来的 需要隐藏header 并且覆盖网站语言
    const { website_locale: websiteLocale } = getParams();
    const results = await init({ beforeInit: hideHeaderHandler, forceSetLocale: websiteLocale });
    if (!results) return;
    const [moduleConfig, globalConfig, locale, headerService, legalConfig] = results;

    const ifFreeView = getIsFreeView();
    const isSpecialIp = get(globalConfig, 'userInfo.isSpecialIp', false);
    const zPREFACE = {
        NAME: 'patent-view',
        globalConfig,
        accountInfo: get(headerService, 'account._info', {}),
        legalConfig,
        pageConfig: {
            whiteLabel: headerService.whiteLabel
        },
        moduleConfig,
        tplLinks: moduleConfig.tplLinks
    };

    const serviceArea = get(zPREFACE, 'accountInfo.site_region', 'CN').toLowerCase();
    window.$bridge.store.write('helpCenterGuiderConfig', {
        module: 'patentView',
        serviceArea
    });

    if (ifFreeView) {
        window.$bridge.store.write('headerConfig.inputType', 'noInput');
    } else {
        // 获取lab功能是否开启
        try {
            if (get(headerService, 'account._info.site_region') === 'CN') {
                const {
                    VUE_APP_LAB_IMAGE_MARK_ANCHOR,
                    VUE_APP_LAB_PATENT_VIEW_FONT_SIZE_ANCHOR,
                    VUE_APP_LAB_DEEP_VIEW_ANCHOR,
                    VUE_APP_LAB_DEEP_CLAIM_SWITCH_ANCHOR,
                    VUE_APP_LAB_HI_RES_IMAGE_ANCHOR,
                } = process.env;
                const usedAnchorInLab = {};
                const mapping = {
                    [VUE_APP_LAB_IMAGE_MARK_ANCHOR]: 'IMAGE_MARK',
                    [VUE_APP_LAB_PATENT_VIEW_FONT_SIZE_ANCHOR]: 'FONT_SIZE',
                    [VUE_APP_LAB_DEEP_VIEW_ANCHOR]: 'PATENT_DEEP_VIEW',
                    // [VUE_APP_LAB_DEEP_CLAIM_SWITCH_ANCHOR]: 'DEEP_CLAIM_SWITCH',
                    [VUE_APP_LAB_HI_RES_IMAGE_ANCHOR]: 'HI_RES_IMAGE',
                };
                const anchors = await getBatchUsedByAnchorApi({ anchors: [VUE_APP_LAB_IMAGE_MARK_ANCHOR, VUE_APP_LAB_PATENT_VIEW_FONT_SIZE_ANCHOR, VUE_APP_LAB_DEEP_VIEW_ANCHOR, VUE_APP_LAB_DEEP_CLAIM_SWITCH_ANCHOR, VUE_APP_LAB_HI_RES_IMAGE_ANCHOR] });
                anchors.forEach(item => {
                    if (mapping[item.anchor]) usedAnchorInLab[mapping[item.anchor]] = { enabled: Boolean(item.used), anchor: item.anchor, app_id: item.app_id, staging: item.staging };
                });
                zPREFACE.usedAnchorInLab = usedAnchorInLab;
            }
        } catch (error) {
            console.warn('getBatchUsedByAnchorApi error', error);
        }
        // 获取lab功能是否开启
    }

    window.zPREFACE = zPREFACE;
    /* 兼容遗留代码 */
    fixZpreface(locale);

    // 初始化路由重定向逻辑
    setRootRedirect(isFamilySearch);

    // init dual-view setting
    let dualViewSetting = {};
    if (!ifFreeView && !isSpecialIp) {
        try {
            dualViewSetting = await getAnalyticsDualViewSetting() || {};
        } catch (e) {
            console.log(e);
        }
    }
    const dualViewDefaultTab = getDualViewDefaultTab(dualViewSetting);
    setDualViewRedirect(dualViewDefaultTab.left, dualViewDefaultTab.right);

    PatentUtils.initTpl();

    // 创建国际化相关内容
    setTitle({ locale, isWhiteLabel: headerService.whiteLabel });
    const locales = await i18n(locale);
    initTransformCodeInterceptor(locales);

    // 创建vue实例
    const vm = new Vue({
        el: '#view-root',
        router,
        store,
        i18n: locales,
        render: h => h(PatentView)
    });

    // 更新权限数据和双视图设置
    if (!isEmpty(moduleConfig.permissions)) {
        permissionService.updatePermission(moduleConfig.permissions as Permission);
    }
    if (!isEmpty(dualViewSetting)) {
        dualViewSettingService.updateSetting(dualViewSetting as DualViewSetting);
    }

    window.hidePageLoading && window.hidePageLoading();
};

startup();
